import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, LinearProgress, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { ImageUploadServices } from '../ImgUpload/imageUploadSrevices';

const DEFAULT_LIMITS = {
  document: 10 * 1024 * 1024, // 10MB
  audio: 20 * 1024 * 1024, // 20MB
  video: 100 * 1024 * 1024, // 100MB
};

/**
 * Global DocumentUpload dialog to upload files (pdf, doc, xls, csv, txt), audio, and video.
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - uploadData: { path: string }
 * - onUploadResponse: (data) => void
 * - fileCategory: 'document' | 'audio' | 'video' (default: 'document')
 * - accept: string (override accept filter)
 * - maxFileSize: number (bytes)
 * - dialogTitle: string
 */
export default function DocumentUploadDialog(props) {
  const {
    open,
    onClose,
    uploadData,
    onUploadResponse,
    fileCategory = 'document',
    accept,
    maxFileSize,
    dialogTitle,
  } = props;

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      folder: uploadData?.path,
      file: null,
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFileSize, setSelectedFileSize] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setSelectedFileName('');
      setSelectedFileSize(0);
      setUploadProgress(0);
    }
  }, [open]);

  const inputAccept = accept || (
    fileCategory === 'video' ? 'video/*' :
    fileCategory === 'audio' ? 'audio/*' :
    '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt'
  );

  const sizeLimit = maxFileSize || DEFAULT_LIMITS[fileCategory] || DEFAULT_LIMITS.document;

  const validateFileType = (file) => {
    if (!file) return true;
    if (fileCategory === 'video') return file.type.startsWith('video/');
    if (fileCategory === 'audio') return file.type.startsWith('audio/');
    // Document: validate by extension for broader compatibility
    const allowedExt = ['pdf','doc','docx','xls','xlsx','csv','txt'];
    const nameParts = file.name.split('.');
    const ext = nameParts[nameParts.length - 1]?.toLowerCase();
    return allowedExt.includes(ext);
  };

  const onSelectedFile = (event, onChange) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      setSelectedFileSize(file.size);
      onChange(file);
    } else {
      setSelectedFileName('');
      setSelectedFileSize(0);
      onChange(null);
    }
  };

  const handleUpload = async (data) => {
    setIsSubmitting(true);
    setUploadProgress(0);
    if (!data.file || !data.folder) {
      setIsSubmitting(false);
      return;
    }
    console.log('Uploading file :', data.file);
    
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('folder', data.folder);
    console.log('formData', formData);
    // return
    try {
      const response = await ImageUploadServices.uploadDocument(formData, (progress) => {
        setUploadProgress(progress);
      });
      console.log('Upload response:', response);
      if (response.success == '1') {
        onUploadResponse(response.data);
      } else {
        Swal.fire('File upload Failed', '', 'error');
      }
    } catch (error) {
      // console.error('Upload failed:', error);
      Swal.fire('File upload Failed', '', 'error');
    } finally {
      onClose();
      setIsSubmitting(false);
      reset();
      setUploadProgress(0);
    }
  };

  return (
    <Modal
      open={open}
      aria-labelledby="document-upload-title"
      aria-describedby="document-upload-description"
      sx={{ overflow: 'scroll' }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '80%', sm: '50%' },
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: 0,
          width: { xs: '95%', sm: '30%' },
          minHeight: '25%',
        }}
        borderRadius={2}
      >
        <Box sx={{ borderBottom: '1px solid lightGray', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '3%' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {dialogTitle || (fileCategory === 'video' ? 'Video Upload' : fileCategory === 'audio' ? 'Audio Upload' : 'File Upload')}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ p: 0 }} color="error">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit(handleUpload)}>
          <Box sx={{ width: '100%', minHeight: '220px', py: '3%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="file"
                  rules={{
                    required: `Please select a ${fileCategory === 'document' ? 'file' : fileCategory}`,
                    validate: {
                      fileType: (value) => {
                        if (!value) return true;
                        return validateFileType(value) || (
                          fileCategory === 'video' ? 'Please upload a valid video file.' :
                          fileCategory === 'audio' ? 'Please upload a valid audio file.' :
                          'Allowed: pdf, doc, docx, xls, xlsx, csv, txt'
                        );
                      },
                      fileSize: (value) => {
                        if (!value) return true;
                        return value.size <= sizeLimit || `File size should be less than ${Math.round(sizeLimit / (1024 * 1024))} MB.`;
                      },
                    },
                  }}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <>
                      <input
                        type="file"
                        accept={inputAccept}
                        id="doc-file"
                        onChange={(event) => onSelectedFile(event, onChange)}
                        style={{ display: 'none' }}
                      />
                      <Box component="label" htmlFor="doc-file" sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button size="small" variant="outlined" component="span" disabled={isSubmitting}>
                          {fileCategory === 'document' ? 'Choose File' : fileCategory === 'video' ? 'Choose Video' : 'Choose Audio'}
                        </Button>
                      </Box>
                      {error && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                {selectedFileName && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '.9rem', fontWeight: 500 }}>{selectedFileName}</Typography>
                    <Typography sx={{ fontSize: '.75rem', color: 'text.secondary' }}>{Math.round(selectedFileSize / 1024)} KB</Typography>
                  </Box>
                )}
              </Grid>
              {isSubmitting && (
                <Grid item xs={12}>
                  <Box sx={{ px: '10%', textAlign: 'center' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 1 }} />
                    <Typography sx={{ fontSize: '.75rem', mt: 1 }}>{uploadProgress}%</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, borderTop: '1px solid lightGray', py: '2%' }}>
            <Button variant="outlined" size="small" type="submit" disabled={errors?.file || isSubmitting}>
              {isSubmitting ? (
                <>
                  <CircularProgress size={24} sx={{ marginRight: 1 }} />
                  Uploading...
                </>
              ) : (
                fileCategory === 'document' ? 'Upload File' : fileCategory === 'video' ? 'Upload Video' : 'Upload Audio'
              )}
            </Button>
            <Button variant="outlined" size="small" onClick={onClose} color="error" disabled={isSubmitting}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
